import { useLocation } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import useRegistrationLoadingBar from "../../_hooks/useRegistrationLoadingBar";
import usePhoneField from "../../_hooks/usePhoneField";
import useAnalytics from "../../_hooks/useAnalytics";
import useTabNavigation from "../../_hooks/useTabNavigation";
import isEmail from "validator/es/lib/isEmail";
import {
    login,
    logout,
    selectOrganizationByLogin,
    setUpResetPassword,
} from "../../user/services/store/authentication.actions";
import { appstoreSetupReducerActions } from "../../_services/store";
import { submitAnalytics } from "../../organization/myOrganization/services/store/myOrganization.actions";
import {
    connectNewShopToExistingOrgAndUser,
    connectNewShopToExistingUserInitialLogin,
    createNewOrgForExistingUser,
    submitNewOrganizationAndUserRegistrationIncludingExistingAuthData,
} from "../../user/services/store/appstoreSetup.actions";
import { ModalInfo } from "../../_UI/Components/ModalInfo";
import AlertComponent from "../../_UI/Components/Alerts/AlertComponent";
import {
    Col,
    Container,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Progress,
    Row,
} from "reactstrap";
import { PathwayCard, PathwayCardBody } from "../../_UI/Components/PathwayCard";
import { PathwayFormGrid } from "../../_UI/StyledComponents/PathwayForm";
import PathwayTextInputGroup from "../../_UI/Components/Inputs/PathwayTextInputGroup";
import { LoadingSpinnerButton, PathwayButton } from "../../_UI/Components/PathwayButtons";
import LoadingSpinner from "../../_UI/Components/LoadingSpinner";
import TabNavigation from "../../_UI/Components/TabModul/TabNavigation";
import TabContentCard from "../../_UI/Components/TabModul/TabContentCard";
import styled from "styled-components";
import TikTokAppstoreNewUserRegistration from "../components/TikTokAppstoreNewUserRegistration";
import TikTokAppstoreExistingUserLogin from "../components/TikTokAppstoreExistingUserLogin";
import listUtils from "../../_services/list-utils";
import { WarningMessage } from "../../_UI/Components/Alerts/PathwayAlertMessages";

const NEW_USER = "NEW_USER";
const EXISTING_USER = "EXISTING_USER";
const TABS = {
    NEW_USER,
    EXISTING_USER,
};
const TAB_LIST = () => {
    return [
        {
            description: "Registrieren",
            identifier: TABS.NEW_USER,
        },
        {
            description: "Login",
            identifier: TABS.EXISTING_USER,
        },
    ];
};

const TikTokRegistrationScreen = () => {
    const location = useLocation();
    const dispatch = useDispatch();
    const queryParameters = useMemo(() => {
        return new URLSearchParams(location.search);
    }, [location.search]);

    const appstoreSetup = useSelector((state) => state.appstoreSetup);
    const authentication = useSelector((state) => state.authentication);
    const [resetMail, setResetMail] = useState("");
    const [resetSuccessMessage, setResetSuccessMessage] = useState(null);
    const [forgetPasswordModal, setForgetPasswordModal] = useState(false);
    const [loginSubmitted, setLoginSubmitted] = useState(false);
    const [createNewOrg, setCreateNewOrg] = useState(false);
    const [emailError, setEmailError] = useState("");
    const [phoneSubmitted, setPhoneSubmitted] = useState(false);

    // Phone is optional in appstore registration; an invalid (non-empty) number blocks submit (create flow).
    const phoneField = usePhoneField("", { submitted: phoneSubmitted });

    const authDataUUID = queryParameters.get("authData");

    const { progress, setProgress, statusText } = useRegistrationLoadingBar(
        appstoreSetup.shopifyAppstoreNewOrgCreationLoading
    );

    const { isProduction, analytics } = useAnalytics();

    const [newOrgAndUser, setNewOrgAndUser] = useState({
        organizationName: "",
        email: "",
        firstName: "",
        lastName: "",
        acceptTos: false,
        acceptDpa: false,
        password: "",
        passwordRepeat: "",
        connectedAuthDataExternalUUID: authDataUUID,
        shopifyAppCustomer: false,
        integration: "TIKTOK",
    });

    const [existingUser, setExistingUser] = useState({
        email: "",
        password: "",
        submitted: false,
        organizationExternalUUID: "",
        selectedOrganizationExternalUUID: "",
        connectedAuthDataExternalUUID: authDataUUID,
        organizationName: "",
        shopifyAppCustomer: false,
        integration: "TIKTOK",
    });

    const { activeTab, displayedDropdownMenu, tabList, changeDisplayDropdownMenu, setDeterminedTabs, setActiveTab } =
        useTabNavigation("", TABS.NEW_USER);

    useEffect(() => {
        setDeterminedTabs(TAB_LIST);
    }, [setDeterminedTabs]);

    const toggleActiveContentCard = (isActiveTab) => {
        if (activeTab !== isActiveTab) {
            setActiveTab(isActiveTab);
        }
    };

    useEffect(() => {
        if (localStorage.getItem("user")) {
            dispatch(logout());
        }
    }, [dispatch]);

    const toggleForgetPasswordModal = useCallback(() => {
        setForgetPasswordModal(!forgetPasswordModal);
        setResetSuccessMessage("");
        setResetMail("");
    }, [forgetPasswordModal, setForgetPasswordModal, setResetSuccessMessage, setResetMail]);

    const handleChange = useCallback(
        (e) => {
            const { name } = e.target;
            const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
            if (name === "email") {
                setEmailError("");
            }
            setNewOrgAndUser({
                ...newOrgAndUser,
                [name]: value,
            });
        },
        [newOrgAndUser, setNewOrgAndUser]
    );

    const handleEmailBlur = useCallback((value) => {
        if (value) {
            const trimmed = value.trim();
            if (trimmed !== value) {
                setNewOrgAndUser((prev) => ({ ...prev, email: trimmed }));
            }
            if (trimmed.includes(" ")) {
                setEmailError("E-Mail-Adresse enthält ungültige Leerzeichen");
            } else if (trimmed && !isEmail(trimmed)) {
                setEmailError("Ungültige E-Mail-Adresse");
            } else {
                setEmailError("");
            }
            return trimmed;
        }
        return value || "";
    }, []);

    const handleSubmitRegistration = async (e, registrationData, submitAction) => {
        dispatch(appstoreSetupReducerActions.clearErrorMessages());

        const trimmedEmail = registrationData.email?.trim();
        if (!trimmedEmail) {
            return dispatch(appstoreSetupReducerActions.setRegistrationError("E-Mail-Adresse ist erforderlich"));
        }
        if (trimmedEmail.includes(" ")) {
            return dispatch(appstoreSetupReducerActions.setRegistrationError("E-Mail-Adresse enthält ungültige Leerzeichen"));
        }
        if (!isEmail(trimmedEmail)) {
            return dispatch(appstoreSetupReducerActions.setRegistrationError("Ungültige E-Mail-Adresse"));
        }
        const trimmedRegistrationData = { ...registrationData, email: trimmedEmail };

        if (
            trimmedRegistrationData.passwordRepeat &&
            trimmedRegistrationData.password &&
            trimmedRegistrationData.password !== trimmedRegistrationData.passwordRepeat
        ) {
            return dispatch(appstoreSetupReducerActions.setPasswordMismatchError());
        }
        try {
            const result = await dispatch(submitAction(trimmedRegistrationData));
            setProgress(100);
            if (isProduction) {
                submitAnalytics(result.externalUUID, analytics);
            }

            try {
                await dispatch(login(trimmedRegistrationData.email, trimmedRegistrationData.password));
                await dispatch(selectOrganizationByLogin(result.externalUUID, "/"));
                dispatch(appstoreSetupReducerActions.closeCreationModal());
            } catch (loginError) {
                dispatch(appstoreSetupReducerActions.setRegistrationError(loginError));
            }
        } catch (submitError) {
            dispatch(appstoreSetupReducerActions.setRegistrationError(submitError));
        }
    };
    const handleSubmitNewOrgRegistration = (e) => {
        setPhoneSubmitted(true);
        if (!phoneField.validForSubmit) {
            return dispatch(appstoreSetupReducerActions.setRegistrationError(phoneField.errorMessage));
        }
        handleSubmitRegistration(
            e,
            { ...newOrgAndUser, phoneNumber: phoneField.value || undefined },
            submitNewOrganizationAndUserRegistrationIncludingExistingAuthData
        );
    };

    const handleResetMailChange = (e) => {
        e.preventDefault();
        const value = e.target.value;
        setResetMail(value);
    };

    const handleExistingUserLoginChange = (e) => {
        e.preventDefault();
        const { name } = e.target;
        const value = e.target.value;
        setExistingUser({
            ...existingUser,
            organizationExternalUUID: queryParameters.get("organizationExternalUUID"),
            [name]: value,
        });
    };

    const submitExistingUserInitialLogin = async () => {
        dispatch(appstoreSetupReducerActions.clearErrorMessages());
        const trimmedEmail = existingUser.email?.trim();
        if (trimmedEmail && !isEmail(trimmedEmail)) {
            return dispatch(appstoreSetupReducerActions.setRegistrationError("Ungültige E-Mail-Adresse"));
        }
        setLoginSubmitted(true);
        dispatch(connectNewShopToExistingUserInitialLogin({ ...existingUser, email: trimmedEmail })).catch((error) => {
            console.error(error);
        });
    };

    const submitSelectedOrganizationAndClient = async (e) => {
        try {
            if (!createNewOrg) {
                console.log("Not creating a new org");
                // add new shop for existing org connected to user
                await handleSubmitRegistration(e, existingUser, connectNewShopToExistingOrgAndUser);
            } else {
                console.log("Creating a new org");
                // create new org for existing user
                await handleSubmitRegistration(e, existingUser, createNewOrgForExistingUser);
            }
        } catch (error) {
            // error
        }
    };

    const handleSelectOrgAndClientChange = (e) => {
        e.preventDefault();
        const { name } = e.target;
        const value = e.target.value;

        if (name === "selectedOrganizationExternalUUID") {
            setCreateNewOrg(value === "createNewOrganization");
        }

        setExistingUser({
            ...existingUser,
            [name]: value,
        });
    };

    const resetPassword = async () => {
        try {
            dispatch(setUpResetPassword(resetMail));
            setResetSuccessMessage(
                "Falls die E-Mail in unserer Datenbank vorhanden ist, werden wir einen Link zum Zurücksetzen an die von dir angegebene E-Mail schicken."
            );
            setResetMail(null);
        } catch (error) {
            // error
        }
    };

    return (
        <div className="app flex-row align-content-center" style={{ minHeight: "100vh", background: "var(--surface-page)" }}>
            <ModalInfo
                isOpen={appstoreSetup.shopifyAppstoreNewOrgModalOpen}
                size={"lg"}
                toggleModal={() => dispatch(appstoreSetupReducerActions.closeCreationModal())}
                disabled={appstoreSetup.shopifyAppstoreNewOrgCreationLoading}
                primaryButtonText="Zurück"
                title={"Fast geschafft..."}
            >
                <>
                    {(authentication.errorMessage || appstoreSetup.shopifyNewUserError) && (
                        <AlertComponent errorMessages={appstoreSetup.shopifyNewUserError} />
                    )}
                    {appstoreSetup.shopifyAppstoreNewOrgCreationLoading && (
                        <Progress
                            className="my-2"
                            value={progress}
                            style={{
                                height: "20px",
                            }}
                        >
                            {statusText}
                        </Progress>
                    )}
                </>
            </ModalInfo>

            <Modal isOpen={forgetPasswordModal} toggle={toggleForgetPasswordModal} centered={true}>
                <ModalHeader>Passwort vergessen</ModalHeader>
                <ModalBody>
                    <PathwayFormGrid>
                        <PathwayTextInputGroup
                            label={"E-Mail"}
                            name={"resetMail"}
                            placeHolder={"E-Mail"}
                            handleChange={handleResetMailChange}
                            value={resetMail}
                            mandatory
                        />
                        <AlertComponent
                            errorMessages={authentication.resetErrorMessage}
                            successMessages={resetSuccessMessage}
                        />
                    </PathwayFormGrid>
                </ModalBody>
                <ModalFooter>
                    <LoadingSpinnerButton
                        onClick={resetPassword}
                        loading={authentication.resetLoading}
                        disabled={resetSuccessMessage || !resetMail}
                    >
                        Passwort zurücksetzen
                    </LoadingSpinnerButton>
                    <PathwayButton color="secondary" onClick={toggleForgetPasswordModal}>
                        {resetMail ? "Abbrechen" : "Schließen"}
                    </PathwayButton>
                </ModalFooter>
            </Modal>

            <Container>
                <Row className="justify-content-center">
                    <Col md="8" xs="16">
                        <PathwayCard style={{ marginTop: "1rem", marginBottom: "1rem" }}>
                            <PathwayCardBody style={{ minHeight: "448px" }}>
                                <Col xs="12" className="justify-content-center">
                                    <img
                                        src="/logo.png"
                                        alt="Pathway Logo"
                                        style={{ maxWidth: "60%" }}
                                        className="rounded mx-auto d-block"
                                    />
                                    <br />
                                    <br />
                                </Col>
                                {(appstoreSetup.shopifyNewUserLoading || authentication.loading) && (
                                    <LoadingContainer>
                                        <LoadingSpinner
                                            loading={appstoreSetup.shopifyNewUserLoading || authentication.loading}
                                        />
                                    </LoadingContainer>
                                )}

                                {listUtils.isEmpty(authDataUUID) && (
                                    <WarningMessage marginless>
                                        <h3>Anmeldung nicht möglich</h3>
                                        Bei der Registrierung ihres Shops trat ein Fehler auf. <br />
                                        Bitte installiere die App erneut.
                                    </WarningMessage>
                                )}
                                {!listUtils.isEmpty(authDataUUID) &&
                                    (!appstoreSetup.shopifyNewUserLoading || !authentication.loading) && (
                                        <>
                                            <TabNavigation
                                                defaultDisplayedDropdownMenu={""}
                                                nameIdentifier={"description"}
                                                tabIdentifier={"identifier"}
                                                tabList={tabList}
                                                displayedDropdownMenu={displayedDropdownMenu}
                                                activeTab={activeTab}
                                                toggleActiveContentCard={toggleActiveContentCard}
                                                changeDisplayDropdownMenu={changeDisplayDropdownMenu}
                                                splitIndex={2}
                                            />
                                            {tabList &&
                                                tabList.map((type, index) => (
                                                    <TabContentCard
                                                        key={index}
                                                        tabList={tabList}
                                                        index={index}
                                                        activeTab={activeTab}
                                                        tabPropertyName={"identifier"}
                                                    >
                                                        {activeTab === TABS.NEW_USER ? (
                                                            <TikTokAppstoreNewUserRegistration
                                                                authDataExisted={false}
                                                                submit={handleSubmitNewOrgRegistration}
                                                                newOrgAndUser={newOrgAndUser}
                                                                handleChange={handleChange}
                                                                handleEmailBlur={handleEmailBlur}
                                                                emailError={emailError}
                                                                phoneInputProps={phoneField.inputProps}
                                                                shopUrl={"replace.me"}
                                                            />
                                                        ) : activeTab === TABS.EXISTING_USER ? (
                                                            <TikTokAppstoreExistingUserLogin
                                                                queryParameters={queryParameters}
                                                                handleExistingUserLoginChange={
                                                                    handleExistingUserLoginChange
                                                                }
                                                                existingUser={existingUser}
                                                                submitExistingUserInitialLogin={
                                                                    submitExistingUserInitialLogin
                                                                }
                                                                submitSelectedOrganizationAndClient={
                                                                    submitSelectedOrganizationAndClient
                                                                }
                                                                toggleForgetPasswordModal={toggleForgetPasswordModal}
                                                                loginSubmitted={loginSubmitted}
                                                                handleSelectOrgAndClientChange={
                                                                    handleSelectOrgAndClientChange
                                                                }
                                                                authDataType={queryParameters.get("authDataType")}
                                                            />
                                                        ) : null}
                                                    </TabContentCard>
                                                ))}
                                        </>
                                    )}
                            </PathwayCardBody>
                        </PathwayCard>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default TikTokRegistrationScreen;

const LoadingContainer = styled.div`
    display: grid;
    justify-content: center;
    align-items: center;
`;
